import type {
  IFillStyle,
  IKuikaStyle,
  IProject,
  IStylingBorder,
  IStylingEffects,
  IStylingPosition,
  IStylingShadow,
  IStylingTypography
} from "@kuika/kuika-cl-model";

/**
 * Read-side helpers for project-level unified style tokens (KOA-15838).
 *
 * A unified style is a single named token whose styling data spans all 5
 * sections — `stylingPosition` / `stylingTypography` / `stylingFill` /
 * `stylingBorder` / `stylingShadow` arrays each carry an entry with the same
 * `groupId`. Authoring (create / delete / rename) lives inside the styling
 * panel UI (`unified-styling-selector.tsx`) and stays there by design. These
 * helpers exist so MCP tool handlers can read tokens and apply them to
 * components without duplicating the 5-collection iteration logic, and
 * without dragging the antd-form-bound StylingPanel component class into the
 * headless handler path.
 *
 * `unified-styling-selector.tsx` keeps its own inline `getUnifiedStyleList`
 * for the UI side: it bundles legacy single-section tokens and exposes a
 * different shape (`IUnifiedStyleItem` with display strings). The
 * duplication is intentional — different consumers want different shapes —
 * but the lookup rules MUST stay in lockstep.
 */

export type UnifiedStyleStateKey = "base" | "hover" | "pressed" | "focused" | "disabled";

export interface UnifiedStyleInfo {
  groupId: string;
  name: string;
  /** Section keys whose project array carries an entry for this groupId. */
  sectionsPresent: ("position" | "text" | "fill" | "border" | "shadow" | "effects")[];
  /** Layers that have at least one non-empty property on at least one section. */
  states: UnifiedStyleStateKey[];
}

export interface UnifiedStyleEntries {
  groupId: string;
  name: string;
  position?: IStylingPosition;
  typography?: IStylingTypography;
  fill?: IFillStyle;
  border?: IStylingBorder;
  shadow?: IStylingShadow;
  effects?: IStylingEffects;
}

interface SectionWithState {
  hoverStyles?: Record<string, unknown>;
  pressedStyles?: Record<string, unknown>;
  focusedStyles?: Record<string, unknown>;
  disabledStyles?: Record<string, unknown>;
}

const STATE_SECTION_KEY: Record<Exclude<UnifiedStyleStateKey, "base">, keyof SectionWithState> = {
  hover: "hoverStyles",
  pressed: "pressedStyles",
  focused: "focusedStyles",
  disabled: "disabledStyles"
};

/**
 * Section keys that count as "non-empty base content" — anything not in this
 * list is metadata (state buckets, ID refs, lock flags, names) and must not
 * make the base layer look populated. Mirrors the exclude logic in
 * `hasBaseStyles` (unified-styling-selector.tsx ~1579) so both sides agree on
 * what a "real" base value is.
 */
const BASE_METADATA_KEYS_BY_SECTION: Record<string, ReadonlySet<string>> = {
  position: new Set([
    "positionId",
    "positionIds",
    "groupId",
    "name",
    "componentType",
    "hoverStyles",
    "pressedStyles",
    "focusedStyles",
    "disabledStyles"
  ]),
  text: new Set([
    "typoId",
    "typoIds",
    "groupId",
    "name",
    "componentType",
    "hoverStyles",
    "pressedStyles",
    "focusedStyles",
    "disabledStyles"
  ]),
  fill: new Set([
    "fillId",
    "fillIds",
    "groupId",
    "name",
    "componentType",
    "isVisible",
    "hoverStyles",
    "pressedStyles",
    "focusedStyles",
    "disabledStyles"
  ]),
  border: new Set([
    "borderId",
    "borderIds",
    "groupId",
    "name",
    "componentType",
    "isVisible",
    "isBorderRadiusLocked",
    "isBorderWidthLocked",
    "hoverStyles",
    "pressedStyles",
    "focusedStyles",
    "disabledStyles"
  ]),
  shadow: new Set([
    "shadowId",
    "shadowIds",
    "groupId",
    "name",
    "componentType",
    "isVisible",
    "hoverStyles",
    "pressedStyles",
    "focusedStyles",
    "disabledStyles"
  ]),
  effects: new Set([
    "effectsId",
    "effectsIds",
    "groupId",
    "name",
    "kuikaid",
    "componentType",
    "isVisible",
    "hoverStyles",
    "pressedStyles",
    "focusedStyles",
    "disabledStyles"
  ])
};

function sectionHasNonEmptyBase(section: Record<string, unknown>, sectionKey: string): boolean {
  const metaKeys = BASE_METADATA_KEYS_BY_SECTION[sectionKey];
  for (const key of Object.keys(section)) {
    if (metaKeys?.has(key)) continue;
    const value = section[key];
    if (value === undefined || value === null || value === "") continue;
    return true;
  }
  return false;
}

function stateBucketIsNonEmpty(section: SectionWithState, state: Exclude<UnifiedStyleStateKey, "base">): boolean {
  const bucket = section[STATE_SECTION_KEY[state]];
  if (!bucket) return false;
  for (const value of Object.values(bucket)) {
    if (value !== undefined && value !== null && value !== "") return true;
  }
  return false;
}

/**
 * Walk all 5 project styling collections and produce one descriptor per
 * unified token (entries grouped by shared `groupId`). Entries lacking a
 * `groupId` (legacy single-section tokens) are ignored — the MCP
 * `apply_unified` op only operates on true 5-section tokens.
 */
export function listProjectUnifiedStyles(project: IProject): UnifiedStyleInfo[] {
  const byGroupId = new Map<string, UnifiedStyleInfo>();
  const sectionsByGroupId = new Map<string, UnifiedStyleEntries>();

  const ingest = (
    sectionKey: "position" | "text" | "fill" | "border" | "shadow" | "effects",
    entries: { groupId?: string | null; name?: string }[] | undefined
  ): void => {
    if (!entries) return;
    for (const entry of entries) {
      const gid = entry.groupId;
      if (!gid) continue;
      let info = byGroupId.get(gid);
      if (!info) {
        info = { groupId: gid, name: entry.name ?? "", sectionsPresent: [], states: [] };
        byGroupId.set(gid, info);
      }
      if (!info.name && entry.name) info.name = entry.name;
      if (!info.sectionsPresent.includes(sectionKey)) info.sectionsPresent.push(sectionKey);

      let bundle = sectionsByGroupId.get(gid);
      if (!bundle) {
        bundle = { groupId: gid, name: info.name };
        sectionsByGroupId.set(gid, bundle);
      }
      const target = sectionKey === "text" ? "typography" : sectionKey;
      (bundle as unknown as Record<string, unknown>)[target] = entry;
    }
  };

  ingest("position", project.stylingPosition);
  ingest("text", project.stylingTypography);
  ingest("fill", project.stylingFill);
  ingest("border", project.stylingBorder);
  ingest("shadow", project.stylingShadow);
  ingest("effects", project.stylingEffects);

  for (const info of byGroupId.values()) {
    const bundle = sectionsByGroupId.get(info.groupId);
    if (!bundle) continue;
    const sectionEntries: { key: string; section: SectionWithState & Record<string, unknown> }[] = [];
    if (bundle.position) sectionEntries.push({ key: "position", section: bundle.position as never });
    if (bundle.typography) sectionEntries.push({ key: "text", section: bundle.typography as never });
    if (bundle.fill) sectionEntries.push({ key: "fill", section: bundle.fill as never });
    if (bundle.border) sectionEntries.push({ key: "border", section: bundle.border as never });
    if (bundle.shadow) sectionEntries.push({ key: "shadow", section: bundle.shadow as never });
    if (bundle.effects) sectionEntries.push({ key: "effects", section: bundle.effects as never });

    const hasBase = sectionEntries.some(({ key, section }) => sectionHasNonEmptyBase(section, key));
    if (hasBase) info.states.push("base");
    for (const state of ["hover", "pressed", "focused", "disabled"] as const) {
      if (sectionEntries.some(({ section }) => stateBucketIsNonEmpty(section, state))) {
        info.states.push(state);
      }
    }
  }

  return Array.from(byGroupId.values());
}

/**
 * Resolve the 5 section entries belonging to a unified style token by its
 * `groupId`. Returns `undefined` when no section carries the groupId.
 */
export function findUnifiedStyleByGroupId(project: IProject, groupId: string): UnifiedStyleEntries | undefined {
  if (!groupId) return undefined;
  const bundle: UnifiedStyleEntries = { groupId, name: "" };
  let found = false;
  const position = project.stylingPosition?.find((s) => s.groupId === groupId);
  if (position) {
    bundle.position = position;
    if (position.name) bundle.name = position.name;
    found = true;
  }
  const typography = project.stylingTypography?.find((s) => s.groupId === groupId);
  if (typography) {
    bundle.typography = typography;
    if (!bundle.name && typography.name) bundle.name = typography.name;
    found = true;
  }
  const fill = project.stylingFill?.find((s) => s.groupId === groupId);
  if (fill) {
    bundle.fill = fill;
    if (!bundle.name && fill.name) bundle.name = fill.name;
    found = true;
  }
  const border = project.stylingBorder?.find((s) => s.groupId === groupId);
  if (border) {
    bundle.border = border;
    if (!bundle.name && border.name) bundle.name = border.name;
    found = true;
  }
  const shadow = project.stylingShadow?.find((s) => s.groupId === groupId);
  if (shadow) {
    bundle.shadow = shadow;
    if (!bundle.name && shadow.name) bundle.name = shadow.name;
    found = true;
  }
  const effects = project.stylingEffects?.find((s) => s.groupId === groupId);
  if (effects) {
    bundle.effects = effects;
    if (!bundle.name && effects.name) bundle.name = effects.name;
    found = true;
  }
  return found ? bundle : undefined;
}

/**
 * Resolve a unified style token by its display name. Names are not unique by
 * contract, so we return the first match across the iteration order of the
 * underlying collections — same precedence rule the styling panel uses.
 */
export function findUnifiedStyleByName(project: IProject, name: string): UnifiedStyleEntries | undefined {
  if (!name) return undefined;
  const collections: ({ groupId?: string | null; name?: string }[] | undefined)[] = [
    project.stylingPosition,
    project.stylingTypography,
    project.stylingFill,
    project.stylingBorder,
    project.stylingShadow,
    project.stylingEffects
  ];
  for (const collection of collections) {
    const match = collection?.find((s) => s.groupId && s.name === name);
    if (match?.groupId) return findUnifiedStyleByGroupId(project, match.groupId);
  }
  return undefined;
}

/**
 * Mutates `styleObj` so the unified token's 5 section style IDs are attached
 * to the component's per-section `*Ids` arrays AND writes the top-level
 * `styleGroupId` / `styleGroupName` markers `IKuikaStyle` defines for active-
 * token tracking. Without those two top-level fields the styling-panel UI's
 * dropdown (consumed at `styling-window.tsx:1296` / `unified-styling-selector.tsx`)
 * has no way to display the attached token as active, so MCP-attached tokens
 * would round-trip silently invisibly. The per-section `*Ids` writes are
 * dedup'd via `pushUnique` so a repeat-apply is a no-op on the arrays; the
 * top-level fields are simply overwritten (the styling-panel write-path does
 * the same — last attached unified token wins).
 *
 * Returns the list of sections it actually touched so callers can decide
 * whether anything changed.
 */
export function applyUnifiedStyleToStyleObj(
  styleObj: IKuikaStyle,
  entries: UnifiedStyleEntries
): ("position" | "text" | "fill" | "border" | "shadow" | "effects")[] {
  const touched: ("position" | "text" | "fill" | "border" | "shadow" | "effects")[] = [];
  const pushUnique = (arr: string[], id: string): boolean => {
    if (arr.includes(id)) return false;
    arr.push(id);
    return true;
  };
  const ensureSection = <T extends Record<string, unknown>>(section: T | undefined, key: keyof IKuikaStyle): T => {
    if (section) return section;
    const created = {} as T;
    (styleObj as Record<string, unknown>)[key as string] = created;
    return created;
  };

  if (entries.position?.positionId) {
    const position = ensureSection(styleObj.position as Record<string, unknown> | undefined, "position");
    const ids = (position.positionIds as string[] | null | undefined) ?? [];
    if (pushUnique(ids, entries.position.positionId)) touched.push("position");
    position.positionIds = ids;
  }
  if (entries.typography?.typoId) {
    const text = ensureSection(styleObj.text as Record<string, unknown> | undefined, "text");
    const ids = (text.typoIds as string[] | null | undefined) ?? [];
    if (pushUnique(ids, entries.typography.typoId)) touched.push("text");
    text.typoIds = ids;
  }
  if (entries.fill?.fillId) {
    const fill = ensureSection(styleObj.fill as Record<string, unknown> | undefined, "fill");
    const ids = (fill.fillIds as string[] | null | undefined) ?? [];
    if (pushUnique(ids, entries.fill.fillId)) touched.push("fill");
    fill.fillIds = ids;
  }
  if (entries.border?.borderId) {
    const border = ensureSection(styleObj.border as Record<string, unknown> | undefined, "border");
    const ids = (border.borderIds as string[] | null | undefined) ?? [];
    if (pushUnique(ids, entries.border.borderId)) touched.push("border");
    border.borderIds = ids;
  }
  if (entries.shadow?.shadowId) {
    const shadow = ensureSection(styleObj.shadow as Record<string, unknown> | undefined, "shadow");
    const ids = (shadow.shadowIds as string[] | null | undefined) ?? [];
    if (pushUnique(ids, entries.shadow.shadowId)) touched.push("shadow");
    shadow.shadowIds = ids;
  }
  if (entries.effects?.effectsId) {
    const effects = ensureSection(styleObj.effects as Record<string, unknown> | undefined, "effects");
    const ids = (effects.effectsIds as string[] | null | undefined) ?? [];
    if (pushUnique(ids, entries.effects.effectsId)) touched.push("effects");
    effects.effectsIds = ids;
  }

  if (touched.length > 0) {
    styleObj.styleGroupId = entries.groupId;
    if (entries.name) styleObj.styleGroupName = entries.name;
  }

  return touched;
}
